import clsx from 'clsx';
import { Link } from '../../components/Link';
import { confidenceBandBg, CONFIDENCE_BANDS } from '../../lib/confidenceBand';
import type { CategoryPages } from './types';

/**
 * Horizontal "barcode" of the category's pages in order. One cell per page,
 * coloured by its top-1 confidence band (green / amber / red). Each cell links
 * to the page; hovering shows page number, category and score. Complements the
 * bubble chart (shape) and the Pages table (detail) without adding a table.
 */
export function PageSequenceStrip({ pages }: { pages: CategoryPages }) {
  if (!pages.length) {
    return null;
  }

  return (
    <div className="mb-4">
      <div className="flex w-full gap-px overflow-hidden rounded-md">
        {pages.map((page, index) => {
          const score = page.classificationScore ?? 0;
          return (
            <Link
              key={page.id}
              to={`/document-pages/${page.id}`}
              title={`Page ${index + 1} · ${page.classificationCategory} · ${Math.round(score * 100)}%`}
              aria-label={`Page ${index + 1}, ${page.classificationCategory}, ${Math.round(score * 100)}% confidence`}
              className={clsx(
                'h-7 min-w-0 flex-1 transition-opacity hover:opacity-70',
                confidenceBandBg(score),
              )}
            />
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-zinc-400">
        <LegendChip className="bg-green-500 dark:bg-green-400" label={`≥ ${CONFIDENCE_BANDS.high * 100}%`} />
        <LegendChip
          className="bg-amber-500 dark:bg-amber-400"
          label={`${CONFIDENCE_BANDS.medium * 100}–${CONFIDENCE_BANDS.high * 100}%`}
        />
        <LegendChip className="bg-red-500 dark:bg-red-400" label={`< ${CONFIDENCE_BANDS.medium * 100}%`} />
        <span className="ml-auto">{pages.length} pages · confidence band</span>
      </div>
    </div>
  );
}

function LegendChip({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={clsx('inline-block h-2.5 w-2.5 rounded-sm', className)} />
      {label}
    </span>
  );
}
